// The scout's CONTEXT answer and its cross-repo MERGE (#3358 shape, #3359 tier).
//
// WHY THIS SHAPE LIVES IN `domain` AND NOT IN THE RUNNER THAT PRODUCES IT. #3358
// wrote it under `apps/runner/src/modes/scout/context.ts` because exactly one party
// touched it: the box built it and the caller read it off the run tail. #3359 adds a
// THIRD party in the middle — an ask spanning repos runs one sub-scout per repo, and
// something has to merge their answers into one. That merge is not a box's work (no
// box holds two checkouts) and it cannot be the caller's (acceptance 2 refuses a
// downstream reader inferring the collapse), so the mothership parses these payloads.
// Three parties is what makes it a wire contract; a second parser at the merge seam
// would be a second opinion about a shape with no checkout left to settle it.
//
// WHAT THE MERGE MAY NOT DO, and it decided the whole design: the ONLY writer of
// `task_run_events` is the fleet event sink relaying what a box streamed
// (`apps/worker/src/fleet/event-sink.ts`). Every row in a run's hash chain is
// box-authored. A merged answer appended there would be a mothership-authored row
// inside a box's audit chain — so the merge produces a VALUE, computed on read from
// the sub-scouts' own events, and authors no event of its own. Nothing here writes.
//
// ONE REPO PER SUB-SCOUT REMAINS TRUE. {@link ScoutContextPayloadSchema} still
// describes exactly one checkout, unchanged from #3358. The merged shape below is a
// tier above it, never a field inside it — and it deliberately mints no cross-repo
// PLAN object: that is #2315's, and `plan_groups` still denormalises one
// `repo_full_name` per row until it lands.

import { z } from 'zod';
import { BLOCKER_NEEDS_MAX_FIELDS } from '../entities/blocker';
import {
  RAW_TASK_ANCHOR_PATH_MAX,
  RAW_TASK_EXCERPT_MAX,
  RAW_TASK_EXCERPTS_MAX,
  RAW_TASK_METRIC_MAX,
} from '../entities/raw-task/evidence';
import {
  RepoSecretNameSchema,
  SECRET_ENVIRONMENTS,
  SecretEnvironment,
} from '../value-objects/project-ingress';
import {
  boundedGrounding,
  type DispatchedBy,
  FILES_TOUCHED_MAX,
  FILES_TOUCHED_PATH_MAX,
  GROUNDING_MAX_FACTS,
  GroundedFactSchema,
} from '../value-objects/task-envelope';

/** The status event one sub-scout's answer rides — the name a caller filters the run tail on. */
export const SCOUT_CONTEXT_EVENT = 'scout_context';

/** The scout's own words about what it found or why it stopped. */
export const BLOCKED_REASON_MAX_CHARS = 500;

/** Ceiling on the groups one scout may propose, and so on the hints one repo contributes. */
export const SCOUT_MAX_GROUPS = 12;

/**
 * Repos one `plan_start` may fan out over.
 *
 * The bound names a failure, not a bill: each repo in the ask holds one box for the
 * life of its survey, and an account's conversable fleet is small (a `no_scout_capacity`
 * refusal is what an empty one already answers). An unbounded `repos` lets one ask
 * take every box the account owns and starve every other lane behind it — including
 * the coding tasks a previous ask already dispatched. Five is the widest fan-out the
 * fleet can hold and still answer a second caller.
 */
export const SCOUT_FANOUT_MAX_REPOS = 5;

/**
 * A hint's problem statement is a POINTER, not the envelope a coding box executes.
 * Clamped rather than refused — see `buildScoutContext` for why this whole
 * projection truncates.
 */
export const HINT_PROBLEM_MAX = 2_000;

/**
 * `dev` | `ci` and never `prod` — a scout's blocked classification may not ask a
 * human for a production credential (`secretEnvironmentForTaskClass` has no `prod`
 * arm, so a `prod` row is never sealed onto an assignment and a form that collected
 * one would collect a value no run can read).
 */
export const BLOCKED_NEEDS_ENVIRONMENTS = [SecretEnvironment.Dev, SecretEnvironment.Ci] as const;

/**
 * Every stored environment a blocker may NOT ask about — {@link SECRET_ENVIRONMENTS}
 * minus the askable ones, DERIVED so a fourth environment that is not askable is
 * named to the model the day it is added rather than the day someone notices.
 */
export const BLOCKED_NEEDS_REFUSED_ENVIRONMENTS: readonly SecretEnvironment[] =
  SECRET_ENVIRONMENTS.filter(
    (e) => !(BLOCKED_NEEDS_ENVIRONMENTS as readonly SecretEnvironment[]).includes(e),
  );

/**
 * WHICH env-var names a human is being asked for. Names only — a value must never
 * appear here, which is what `classifyBlocked` (`apps/runner`) gates on the way in.
 *
 * NOT the blocker's form. This is the scout's own classification, stored on its
 * `scout_context` run events, and it stays `{ env_keys, environment }`: a scout
 * only ever asks for env secrets in one environment. The blocker it raises carries
 * each name as one `env_secret` field of `BlockerNeedsSchema` v2, so the bound is
 * the form's — every scout ask fits one form.
 */
export const BlockedNeedsSchema = z.object({
  /** Env-var NAMES the work needs. Names only — a value must never appear here. */
  env_keys: z.array(RepoSecretNameSchema).min(1).max(BLOCKER_NEEDS_MAX_FIELDS),
  environment: z.enum(BLOCKED_NEEDS_ENVIRONMENTS),
});
export type BlockedNeeds = z.infer<typeof BlockedNeedsSchema>;

/** One place the work could be cut — a proposed group, minus everything only a mint needs. */
export const SplitHintSchema = z.object({
  /** Plan-local id (`g1`), so `depends_on` below is readable without a second lookup. */
  id: z.string().min(1).max(64),
  title: z.string().min(1).max(300),
  problem: z.string().min(1).max(HINT_PROBLEM_MAX),
  /**
   * Advisory blast radius the brain derived — never a promise about what will change.
   * Bounded per path by the envelope's own `FILES_TOUCHED_PATH_MAX`.
   */
  files_touched: z.array(z.string().min(1).max(FILES_TOUCHED_PATH_MAX)).max(FILES_TOUCHED_MAX),
  /** Sibling hint ids that must land first — the sequential half of "parallel or sequential". */
  depends_on: z.array(z.string().min(1).max(64)).max(SCOUT_MAX_GROUPS),
});
export type SplitHint = z.infer<typeof SplitHintSchema>;

/** One handoff's `work`, and separately its `why` — the survey filing wire's own cap. */
export const SCOUT_CONTEXT_HANDOFF_MAX_CHARS = 300;

// ── the scheduled checkup (#5376) ────────────────────────────────────────────

/**
 * `dispatched_by.source` of a task a repo's `schedules:` entry minted — the scheduled
 * checkup. ONE spelling for both halves: the mothership stamps it at fire time
 * (`apps/worker/src/cron/schedules.ts`) and the box reads it back ({@link
 * isScheduleDispatch}) to seat the checkup doctrine and hold its filings to
 * {@link CheckupFindingFieldsSchema}. Apps never import apps, so it lives here.
 */
export const SCHEDULE_DISPATCH_SOURCE = 'schedule';

/** Was this task minted by a repo's schedule — `{ kind: 'system', source: 'schedule' }`? */
export function isScheduleDispatch(dispatchedBy: DispatchedBy): boolean {
  return dispatchedBy.kind === 'system' && dispatchedBy.source === SCHEDULE_DISPATCH_SOURCE;
}

/** One checkup anchor, `<finding-kind>:<subject>`. Past this it is a sentence, not an id. */
export const CHECKUP_ANCHOR_MAX = 120;
/**
 * A lowercase finding kind, a colon, then a subject that does not open on whitespace —
 * `flaky-test:checkout-e2e`. `.` never matches a newline, so an anchor is one line.
 */
export const CHECKUP_ANCHOR_PATTERN = /^[a-z0-9_-]+:[^\s].*$/;
/** Repo-relative paths one checkup finding says its fix touches. */
export const CHECKUP_PATHS_MAX = 20;
/**
 * The four fields a scheduled checkup's filing carries beyond the survey's three (#5376).
 * A STORED SHAPE: the box writes them on the `raw_task_filed` wire, the mothership maps
 * them into the raw task, and a schedule filing missing any one is refused on BOTH
 * halves (the runner's writer floor, the worker's ingest floor).
 *
 * - `anchor` — the finding's identity across nights. The same problem keeps the same
 *   anchor, so ingest recognises it instead of filing it again (the dedupe key hashes the
 *   anchor, never the reworded prose).
 * - `paths` — the repo-relative files the fix would touch.
 * - `metric` — the one health number the fix should move.
 * - `evidence` — counts, the window they cover, the query or link they came from.
 */
export const CheckupFindingFieldsSchema = z.object({
  anchor: z.string().max(CHECKUP_ANCHOR_MAX).regex(CHECKUP_ANCHOR_PATTERN),
  paths: z.array(z.string().min(1).max(RAW_TASK_ANCHOR_PATH_MAX)).min(1).max(CHECKUP_PATHS_MAX),
  metric: z.string().min(1).max(RAW_TASK_METRIC_MAX),
  // The raw task's OWN excerpt caps, because each line is stored as one excerpt: a wider
  // wire cap would only be a cut the reader makes silently.
  evidence: z.array(z.string().min(1).max(RAW_TASK_EXCERPT_MAX)).min(1).max(RAW_TASK_EXCERPTS_MAX),
});
export type CheckupFindingFields = z.infer<typeof CheckupFindingFieldsSchema>;

/**
 * ONE piece of work the survey noticed OUTSIDE the question it was asked (`hive.md` rule
 * 10), handed back to the caller rather than filed.
 *
 * A `return_context` caller writes the plan itself — that is the whole point of the
 * flag — so the work a scout noticed beside the question is part of the ANSWER. Filing
 * it as raw tasks put it on the triage board behind the caller's back: tesote task
 * tsk_951ecd6cd1b6458e891bca265a659564 (2026-09-19) delivered its context AND four
 * manual tasks triage cut from its handoffs. The same three fields, under the same
 * caps, as the `raw_task_filed` survey wire, so a caller that does want one filed
 * files the same three fields the pump would have — and NOT the pump's two evidence
 * carriers.
 *
 * NOT THE WHOLE FILING, THOUGH. That wire now also carries the finding’s SUPPORTING
 * REFERENCES (`refs` — the lines it stood on, under this handoff’s own path) and its
 * FALSIFIED PREMISES (`excerpts`), and the context answer carries NEITHER: they are
 * the evidence a FILED ask cites, while this answer is prose the caller reads and
 * plans from. The pump is what mints the raw task, and the pump is what carries them.
 *
 * The four {@link CheckupFindingFieldsSchema} fields are OPTIONAL here: the box writes
 * them on a scheduled checkup’s handoffs and on nothing else, so every other payload
 * keeps the three-field shape byte for byte.
 */
export const ScoutContextHandoffSchema = z.object({
  /** The repo-relative path the work is IN — the routing key, verified by the survey. */
  for_path: z.string().min(1).max(RAW_TASK_ANCHOR_PATH_MAX),
  /** What needs doing there. */
  work: z.string().min(1).max(SCOUT_CONTEXT_HANDOFF_MAX_CHARS),
  /** What breaks if nobody does it. */
  why: z.string().min(1).max(SCOUT_CONTEXT_HANDOFF_MAX_CHARS),
  ...CheckupFindingFieldsSchema.partial().shape,
});
export type ScoutContextHandoff = z.infer<typeof ScoutContextHandoffSchema>;

/**
 * ONE sub-scout's answer, as it reaches the caller off `task run_tail`.
 *
 * Parsed on the way out (`buildScoutContext`, `apps/runner`) rather than trusted:
 * the box is the producer, and a payload that does not hold its own shape is a bug
 * we want to see on the box's audit chain, not one the caller's AI discovers as a
 * missing field. Parsed again at the merge seam below for the same reason.
 */
export const ScoutContextPayloadSchema = z.object({
  event: z.literal(SCOUT_CONTEXT_EVENT),
  task_id: z.string().min(1),
  /** The one repo this run had on disk. Plural repos merge one tier up, never here. */
  repo: z.string().min(1),
  /**
   * The commit this scout's tree was at when it read the repo (#5105) — FULL 40-hex,
   * the print of `git rev-parse FETCH_HEAD` (`apps/runner/src/checkout.ts`): GitHub
   * objects are SHA-1, and the plan-file door compares this value against GitHub. The
   * door (#5129, epic #5100) judges staleness from it — a cited file changed or
   * disappeared since the scout read it.
   *
   * OPTIONAL ON PURPOSE, for the two readers that cannot be upgraded in lockstep:
   * - Stored payloads already exist, and `listScoutFanoutMembers`
   *   (`packages/db/src/queries/scout-fanout.ts`) `safeParse`s every stored row. A
   *   failed parse becomes `payload: null`, which means "has not answered" — a
   *   required field would erase every historical answer from the fan-out merge.
   * - Boxes run a released binary that lags main by a release plus a self-update, so
   *   answers keep arriving from producers that predate the field.
   *
   * It is ABSENT ONLY WHEN NO READ SHA IS PROVEN (an unproven tree at the context
   * exit — only the exempt floor brain reaches it that way,
   * `apps/runner/src/modes/scout/executor.ts`). Never defaulted, never an empty
   * string, never a fallback value: the plan-file door refuses a scout answer that
   * has none.
   */
  read_sha: z
    .string()
    .regex(/^[0-9a-f]{40}$/)
    .optional(),
  /** The scout's own one-line account of what it found. */
  summary: z.string().min(1).max(BLOCKED_REASON_MAX_CHARS),
  /** The survey's anchored facts — the relevant files and modules, each with its citations. */
  files: z.array(GroundedFactSchema).max(GROUNDING_MAX_FACTS),
  split_hints: z.array(SplitHintSchema).max(SCOUT_MAX_GROUPS),
  /** The env-var NAMES the work needs and the box does not have. Present only when the run classified one. */
  needs: BlockedNeedsSchema.optional(),
  /** Present only when the scout found the work cannot start — its kind and its own words. */
  blocked: z
    .object({
      kind: z.string().min(1).max(64),
      reason: z.string().min(1).max(BLOCKED_REASON_MAX_CHARS),
      /** Groups the model proposed BESIDE the blocker, which that arm discards. Always present, zero included. */
      dropped_groups: z.number().int().nonnegative(),
    })
    .optional(),
  /**
   * The survey's admissible handoffs — the same selection the survey filing uses (anchor
   * floor, verified routing path, duplicates collapsed). Present when a survey ran, empty
   * included; absent when none did. NO COUNT, by the owner ruling that deleted the
   * filing's (2026-09-16): one scout is already capped at `SCOUT_MAX_HANDOFFS`.
   */
  handoffs: z.array(ScoutContextHandoffSchema).optional(),
});
export type ScoutContextPayload = z.infer<typeof ScoutContextPayloadSchema>;

// ── the cross-repo tier (#3359) ──────────────────────────────────────────────

/** The merged answer's event name — distinct from `scout_context` because it has a distinct producer. */
export const MERGED_SCOUT_CONTEXT_EVENT = 'scout_context_merged';

/**
 * Where one repo in the fan-out stands.
 *
 * `pending` is a FIRST-CLASS state and not an omission, because acceptance 3 refuses
 * a silently smaller answer: a caller reading a merged payload has to be able to
 * tell "this repo contributed nothing" from "this repo has not answered yet", and
 * the two lead to opposite next moves (write the ask vs. poll again).
 */
export const FanoutRepoState = {
  /** The sub-scout emitted its `scout_context`; its findings are in the merge. */
  Answered: 'answered',
  /** The sub-scout has not answered yet. Nothing of this repo is in the merge. */
  Pending: 'pending',
} as const;
export const FanoutRepoStateSchema = z.enum(FanoutRepoState);
export type FanoutRepoState = z.infer<typeof FanoutRepoStateSchema>;

/** One repo's participation in the fan-out — its address, and whether it has answered. */
export const FanoutRepoSchema = z.object({
  repo: z.string().min(1),
  /** The sub-scout task surveying it — the address a caller tails for this repo alone. */
  task_id: z.string().min(1),
  state: FanoutRepoStateSchema,
});
export type FanoutRepo = z.infer<typeof FanoutRepoSchema>;

/**
 * A split hint in the merged answer: the sub-scout's own hint, with its id and edges
 * moved into a per-repo namespace.
 *
 * WHY THE ID IS REWRITTEN. Every sub-scout numbers its groups from `g1`, so an
 * unqualified merge collides two different pieces of work under one id and silently
 * re-points `depends_on`. The rule is stated on the data — {@link fanoutHintId} — so
 * a reader resolves an edge by string equality rather than by knowing the rule.
 *
 * EDGES DO NOT CROSS REPOS. A hint may only depend on a sibling from its OWN repo:
 * the cross-repo ordering object is #2315's, and inventing one here would mint an
 * edge no tier below can honour.
 */
export const MergedSplitHintSchema = SplitHintSchema.extend({
  /** The repo this hint came from — attribution survives the merge. */
  repo: z.string().min(1),
});
export type MergedSplitHint = z.infer<typeof MergedSplitHintSchema>;

/**
 * The merged answer over 1..n sub-scouts.
 *
 * Every field is a projection of the sub-scouts' own payloads. Nothing here is
 * inferred from the ask, and a repo that has not answered contributes nothing but
 * its `pending` row above.
 */
export const MergedScoutContextSchema = z.object({
  event: z.literal(MERGED_SCOUT_CONTEXT_EVENT),
  /** The address the caller polls — the id every sub-scout envelope in this fan-out carries. */
  fanout_id: z.string().min(1),
  /** Every repo in the ask, answered or not, in the order the caller named them. */
  repos: z.array(FanoutRepoSchema).min(1).max(SCOUT_FANOUT_MAX_REPOS),
  /** One line per answered repo, in `repos` order. */
  summary: z.string().min(1),
  /**
   * The anchored facts of every answered repo, deduped by claim.
   *
   * Refs are REPO-QUALIFIED (`repo:path:line`) before the collapse, so a fact two
   * repos both established keeps both repos' citations and a reader never has to
   * guess which checkout a `path:line` belongs to.
   */
  files: z.array(GroundedFactSchema).max(GROUNDING_MAX_FACTS),
  /** Every answered repo's hints, ids namespaced per {@link fanoutHintId}. */
  split_hints: z.array(MergedSplitHintSchema).max(SCOUT_FANOUT_MAX_REPOS * SCOUT_MAX_GROUPS),
  /** Per-repo, the env-var names that repo's work needs. Absent when no repo classified one. */
  needs: z
    .array(z.object({ repo: z.string().min(1), needs: BlockedNeedsSchema }))
    .max(SCOUT_FANOUT_MAX_REPOS)
    .optional(),
  /** Per-repo, the repos whose work cannot start. Absent when none is blocked. */
  blocked: z
    .array(
      z.object({
        repo: z.string().min(1),
        kind: z.string().min(1).max(64),
        reason: z.string().min(1).max(BLOCKED_REASON_MAX_CHARS),
      }),
    )
    .max(SCOUT_FANOUT_MAX_REPOS)
    .optional(),
  /** Every answered repo's handoffs, attributed. Absent when no repo returned one. */
  handoffs: z.array(ScoutContextHandoffSchema.extend({ repo: z.string().min(1) })).optional(),
});
export type MergedScoutContext = z.infer<typeof MergedScoutContextSchema>;

/**
 * The hint id a merged answer uses for `hint` from `repo`.
 *
 * `repo#id`, and the separator is deliberate: `#` cannot occur in a GitHub
 * `owner/name`, so the qualification is reversible by the reader and can never
 * collide with a repo whose name happens to contain the separator.
 */
export function fanoutHintId(repo: string, hintId: string): string {
  return `${repo}#${hintId}`;
}

/**
 * The citation a merged answer uses for `ref` from `repo`.
 *
 * The same rule, applied to grounding refs, and it is what makes the claim-level
 * dedupe below safe: two repos that both establish "the retry ladder is capped at
 * 60s" collapse to ONE fact, and its refs still say which file in which repo each
 * came from.
 */
export function fanoutRef(repo: string, ref: string): string {
  return `${repo}:${ref}`;
}

/** One sub-scout's contribution to a merge: its repo, its address, and its answer if it has one. */
export type FanoutMember = {
  readonly repo: string;
  readonly taskId: string;
  /** The parsed `scout_context` payload, or null while the sub-scout has not answered. */
  readonly payload: ScoutContextPayload | null;
};

/**
 * Merge 1..n sub-scout answers into one.
 *
 * PURE, and total over any mix of answered and pending members — a caller polls this
 * while the fan-out is still running and gets a partial answer that SAYS it is
 * partial, rather than an error or a short answer that reads complete.
 *
 * THE DEDUPE RULE IS `boundedGrounding`, unchanged and deliberately not re-stated
 * here: it collapses a repeated claim and MERGES that claim's anchors, which is
 * already how the intra-repo hive handles two sub-scouts reaching one conclusion
 * from different files (`surveyGrounding`, `apps/runner/src/survey-rounds.ts`).
 * Cross-repo is the identical question one tier up, so it gets the identical rule
 * rather than a second one that could disagree with it. Qualifying the refs first is
 * the only addition, and it is what keeps attribution through the collapse.
 *
 * A single-member fan-out is not a special case: it merges to the same shape, so a
 * caller that grew from one repo to two changes nothing about how it reads.
 */
export function mergeScoutContexts(input: {
  readonly fanoutId: string;
  readonly members: readonly FanoutMember[];
}): MergedScoutContext {
  const { fanoutId, members } = input;
  const answered = members.filter(
    (m): m is FanoutMember & { payload: ScoutContextPayload } => m.payload !== null,
  );

  const needs = answered
    .filter((m) => m.payload.needs !== undefined)
    .map((m) => ({ repo: m.repo, needs: m.payload.needs as BlockedNeeds }));
  const blocked = answered
    .filter((m) => m.payload.blocked !== undefined)
    .map((m) => ({
      repo: m.repo,
      kind: (m.payload.blocked as { kind: string }).kind,
      reason: (m.payload.blocked as { reason: string }).reason,
    }));

  const handoffs = answered.flatMap((m) =>
    (m.payload.handoffs ?? []).map((handoff) => ({ repo: m.repo, ...handoff })),
  );

  return MergedScoutContextSchema.parse({
    event: MERGED_SCOUT_CONTEXT_EVENT,
    fanout_id: fanoutId,
    repos: members.map((m) => ({
      repo: m.repo,
      task_id: m.taskId,
      state: m.payload === null ? FanoutRepoState.Pending : FanoutRepoState.Answered,
    })),
    summary: fanoutSummary(members),
    files: boundedGrounding(
      answered.flatMap((m) =>
        m.payload.files.map((fact) => ({
          claim: fact.claim,
          refs: fact.refs.map((ref) => fanoutRef(m.repo, ref)),
        })),
      ),
    ),
    split_hints: answered.flatMap((m) =>
      m.payload.split_hints.slice(0, SCOUT_MAX_GROUPS).map((hint) => ({
        ...hint,
        repo: m.repo,
        id: fanoutHintId(m.repo, hint.id),
        // Edges stay inside the repo that authored them — see the schema note.
        depends_on: hint.depends_on.map((dep) => fanoutHintId(m.repo, dep)),
      })),
    ),
    ...(needs.length > 0 ? { needs } : {}),
    ...(blocked.length > 0 ? { blocked } : {}),
    ...(handoffs.length > 0 ? { handoffs } : {}),
  });
}

/**
 * The one line a caller reads before deciding whether to poll again.
 *
 * Names the pending repos rather than only counting them: "1 of 3 still surveying"
 * sends a caller to a repo list to work out which, and the whole point of this
 * answer is that no follow-up question is needed.
 */
function fanoutSummary(members: readonly FanoutMember[]): string {
  const pending = members.filter((m) => m.payload === null);
  const answered = members.length - pending.length;
  const head = `Scouted ${answered} of ${members.length} repo${members.length === 1 ? '' : 's'}`;
  if (pending.length === 0) return `${head}. Nothing dispatched.`;
  return `${head} — still surveying: ${pending.map((m) => m.repo).join(', ')}. Nothing dispatched.`;
}

/**
 * Is this fan-out complete? True when every member has answered.
 *
 * Stated as a function rather than left to each reader's `every`, because "complete"
 * is the condition a caller stops polling on and it must mean one thing.
 */
export function fanoutComplete(members: readonly FanoutMember[]): boolean {
  return members.every((m) => m.payload !== null);
}
